import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUILabels } from '../context/UILabelsContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Send as SendIcon, Files, UsersRound, ArrowRight, Package, ShieldPlus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

export default function SendPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const mode = searchParams.get('mode');

  // Redirect to sub-pages based on mode
  useEffect(() => {
    if (mode === 'single') navigate(getTenantPath('/send/single'));
    else if (mode === 'batch') navigate(getTenantPath('/send/batch'));
    else if (mode === 'bulk') navigate(getTenantPath('/send/bulk'));
    else if (mode === 'packet') navigate(getTenantPath('/send/packet'));
  }, [mode, navigate, getTenantPath]);

  // Show mode selection — rendered inside DashboardLayout so
  // UILabelsProvider is an ancestor and useUILabels() works.
  return (
    <DashboardLayout>
      <SendPageContent navigate={navigate} getTenantPath={getTenantPath} />
    </DashboardLayout>
  );
}

function SendPageContent({ navigate, getTenantPath }) {
  const uiLabels = useUILabels();
  return (
      <div className="space-y-6" data-testid="send-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Send Documents</h1>
          <p className="text-muted-foreground font-body mt-1">
            Choose how you want to send onboarding documents
          </p>
        </div>

        {/* Mode Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-primary transition-all group"
            onClick={() => navigate(getTenantPath('/send/single'))}
            data-testid="mode-single"
          >
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                <SendIcon className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-lg">Single Send</CardTitle>
              <CardDescription>
                Send one document to one recipient
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Select a template</li>
                <li>• Choose a candidate</li>
                <li>• Pre-fill fields</li>
                <li>• Send with custom message</li>
              </ul>
              <div className="mt-4 flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
                Get started <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-purple-500 transition-all group"
            onClick={() => navigate(getTenantPath('/send/packet'))}
            data-testid="mode-packet"
          >
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-3 group-hover:bg-purple-500/20 transition-colors">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle className="text-lg">Send Packet</CardTitle>
              <CardDescription>
                Send a pre-defined packet of documents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Choose a packet</li>
                <li>• Select recipient</li>
                <li>• Sends all docs at once</li>
                <li>• Custom email message</li>
              </ul>
              <div className="mt-4 flex items-center text-purple-600 font-medium text-sm group-hover:gap-2 transition-all">
                Get started <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-amber-500 transition-all group"
            onClick={() => navigate(getTenantPath('/send/batch'))}
            data-testid="mode-batch"
          >
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-3 group-hover:bg-amber-500/20 transition-colors">
                <Files className="w-6 h-6 text-amber-600" />
              </div>
              <CardTitle className="text-lg">Batch Send</CardTitle>
              <CardDescription>
                Pick multiple documents ad-hoc
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Select multiple templates</li>
                <li>• One recipient gets all docs</li>
                <li>• Flexible selection</li>
                <li>• Up to 10 documents</li>
              </ul>
              <div className="mt-4 flex items-center text-amber-600 font-medium text-sm group-hover:gap-2 transition-all">
                Get started <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-green-500 transition-all group"
            onClick={() => navigate(getTenantPath('/send/bulk'))}
            data-testid="mode-bulk"
          >
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-3 group-hover:bg-green-500/20 transition-colors">
                <UsersRound className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-lg">Bulk Send</CardTitle>
              <CardDescription>
                Send one document to many people
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Select one template</li>
                <li>• Add multiple recipients</li>
                <li>• Mass send in one click</li>
                <li>• Progress tracking</li>
              </ul>
              <div className="mt-4 flex items-center text-green-600 font-medium text-sm group-hover:gap-2 transition-all">
                Get started <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-lg hover:border-emerald-500 transition-all group"
            onClick={() => navigate(getTenantPath('/send/credentials'))}
            data-testid="mode-credentials"
          >
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-3 group-hover:bg-emerald-500/20 transition-colors">
                <ShieldPlus className="w-6 h-6 text-emerald-600" />
              </div>
              <CardTitle className="text-lg">Request {uiLabels.credentials}</CardTitle>
              <CardDescription>
                Ask for {uiLabels.credentials.toLowerCase()} — no PDF needed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Pick Bullhorn categories</li>
                <li>• Candidate uploads files</li>
                <li>• Writes to {uiLabels.credentials} tab</li>
                <li>• Auto-expiry reminders</li>
              </ul>
              <div className="mt-4 flex items-center text-emerald-600 font-medium text-sm group-hover:gap-2 transition-all">
                Get started <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
  );
}

